/**
 * Copyright IBM Corp. 2022, 2026
 */

import { vercelSplit } from '@web/cms'

export const dateConfig: Intl.DateTimeFormatOptions = {
	timeZone: 'America/Los_Angeles',
	hour12: true,
}

export const createDate = (start: string, end: string): string => {
	const startDate = new Date(start)
	const endDate = new Date(end)
	const date = getDate(startDate)
	const startTime = getTime(startDate)
	const endTime = getTime(endDate)
	return `${date} | ${startTime} - ${endTime}`
}

export const createDateText = (start: string): string => {
	const startDate = new Date(start)

	return startDate.toLocaleString('en-US', {
		...dateConfig,
		month: 'long',
		day: 'numeric',
	})
}

export const getDate = (date: Date): string => {
	return date.toLocaleString('en-US', {
		...dateConfig,
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	})
}

export const getTime = (date: Date): string => {
	return date.toLocaleString('en-US', {
		...dateConfig,
		timeZoneName: 'shortGeneric',
		hour: 'numeric',
		minute: 'numeric',
	})
}

export const getDateSlugFromStartTime = (startTime: string) => {
	const date = new Date(startTime)
	const month = date.toLocaleString('en-US', {
		...dateConfig,
		month: 'numeric',
	})
	const day = date.toLocaleString('en-US', {
		...dateConfig,
		day: 'numeric',
	})
	return `${month}-${day}`
}

export const productTypeOverrides = {
	'Infrastructure Lifecycle Management': 'ILM',
	'Security Lifecycle Management': 'SLM',
	'HashiCorp Cloud Platform': 'HCP',
}

const VALID_CONFERENCE_NAMES = ['hashidays', 'hashiconf'] as const
export type TConfName = (typeof VALID_CONFERENCE_NAMES)[number]

export const getConfName = (conferenceName: string): TConfName | null =>
	VALID_CONFERENCE_NAMES.includes(conferenceName as TConfName)
		? (conferenceName as TConfName)
		: null

export const getBadgeColor = (text: string): string => {
	const badgeColorOverrides = {
		Hashicorp: 'var(--mds-color-hashicorp-foreground)',
		Boundary: 'var(--mds-color-boundary-foreground)',
		Consul: 'var(--mds-color-consul-foreground)',
		Nomad: 'var(--mds-color-nomad-foreground)',
		Packer: 'var(--mds-color-packer-foreground)',
		Terraform: 'var(--mds-color-terraform-foreground)',
		Vault: 'var(--mds-color-vault-foreground)',
		Vagrant: 'var(--mds-color-vagrant-foreground)',
		Waypoint: 'var(--mds-color-waypoint-foreground)',
		'Infrastructure Lifecycle Management':
			'var(--mds-color-terraform-foreground)',
		ILM: 'var(--mds-color-terraform-foreground)',
		'Security Lifecycle Management': 'var(--mds-color-vault-foreground)',
		SLM: 'var(--mds-color-vault-foreground)',
		'HashiCorp Cloud Platform': 'var(--mds-color-foreground-primary)',
		HCP: 'var(--mds-color-foreground-primary)',
	}

	return badgeColorOverrides[text]
		? badgeColorOverrides[text]
		: 'var(--mds-color-foreground-action)'
}

export interface IBadge {
	text: string
	icon: string
	color?: string
}

export const getConferenceBadgeProps = (badge: IBadge): IBadge => {
	return {
		icon: badge.icon ? vercelSplit(badge.icon) : null,
		text: badge.text ? vercelSplit(badge.text) : null,
		color: getBadgeColor(vercelSplit(badge.text)),
	}
}
